import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock } from "@/components/InnerBlock";

const CR_NUMBER = "1653208";

export default function TermsOfService() {
  return (
    <div data-testid="terms-page">
      <Seo title="Terms of Service" description="Terms governing use of the Wafa Solutions website." />
      <PageHero
        eyebrow="Legal"
        h1="Terms of Service"
        intro="These terms govern use of this website only. Client projects are governed by a separate signed agreement, which prevails where the two differ."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Terms of Service" }]}
      />

      <InnerBlock tint="white">
        <article className="mx-auto max-w-3xl space-y-10 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">1. These terms</h2>
            <p className="mt-4">
              These terms govern your use of this website. By using it you accept them. We may update them; the date above shows when they last changed.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">2. Who we are</h2>
            <p className="mt-4">
              Wafa Solutions LLC, a technology company registered in the Sultanate of Oman under commercial registration number {CR_NUMBER}, and the technology partner to Altalib Group.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">3. Using the website</h2>
            <p className="mt-4">
              You may use this website for lawful purposes connected with evaluating or receiving our services. You must not:
            </p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>use the site in any way that breaches Omani law;</li>
              <li>attempt to gain unauthorised access to any part of the site, its servers or connected systems;</li>
              <li>introduce malicious code or interfere with the site's operation;</li>
              <li>scrape, harvest or systematically extract content;</li>
              <li>use the site to send unsolicited commercial communications;</li>
              <li>misrepresent your identity or affiliation.</li>
            </ul>
            <p className="mt-4">We may suspend or withdraw access without notice.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">4. The information on this site</h2>
            <p className="mt-4">
              We take care to keep the site accurate but make no warranty that it is complete, accurate or up to date.
            </p>
            <p className="mt-4">
              <strong>Nothing on this website is professional advice</strong> — our articles and guides, including anything we publish about the Personal Data Protection Law or governance and risk, is general information, not legal, regulatory, financial or technical advice for your circumstances.
            </p>
            <p className="mt-4">
              <strong>Nothing on this website is an offer</strong> — published prices, timelines and cost bands are indicative, and a binding commitment arises only from a written proposal signed by both parties.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">5. Our intellectual property</h2>
            <p className="mt-4">
              All content on this website is owned by or licensed to Wafa Solutions LLC and protected under Omani law and international treaties. "Wafa Solutions" and "Meyar", together with our other product names and our logos, are our marks; you may not use them without our written permission.
            </p>
            <p className="mt-4">You may read the site, print or download extracts for your own reference, and share links.</p>
            <p className="mt-4">You may not:</p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>reproduce, republish or redistribute our content commercially;</li>
              <li>remove attribution;</li>
              <li>use our content to train a machine learning model without our written permission;</li>
              <li>present our material as your own.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">6. Intellectual property in client work</h2>
            <p className="mt-4"><em>This is a summary only; your signed project agreement governs.</em></p>
            <p className="mt-4">
              On full payment of all sums due, the custom application code, designs and documentation we create specifically for you become your property.
            </p>
            <p className="mt-4">
              We build on our own pre-existing frameworks, libraries, accelerators and platform components, developed at our own cost and reused across clients; these remain our property, and you receive a perpetual, non-exclusive, non-transferable licence to use them as part of your delivered solution. This is what keeps your project price down — you are not paying us to rebuild what already exists.
            </p>
            <p className="mt-4">
              Meyar and our other platforms are our products, licensed on their own terms; engaging us for services does not give you rights in them.
            </p>
            <p className="mt-4">
              Some solutions include open-source or third-party licensed components governed by their own licences, which we will identify to you. We will not name you or describe your project publicly without your written permission.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">7. Your submissions</h2>
            <p className="mt-4">
              If you send us material through the site, you confirm you have the right to send it and grant us permission to use it for the purpose you sent it for. Do not send us confidential information through the website. If you need confidentiality before you can describe your project, ask us for a non-disclosure agreement first — we sign them routinely and at no cost.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">8. Linked sites</h2>
            <p className="mt-4">
              We link to other websites for convenience. We do not control them, do not endorse them, and are not responsible for their content or their handling of your data.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">9. Availability</h2>
            <p className="mt-4">
              We aim to keep the site available but do not guarantee uninterrupted access, and may suspend or restrict it without notice.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">10. Liability</h2>
            <p className="mt-4">
              Nothing in these terms excludes or limits liability where the law does not permit exclusion, including for death or personal injury caused by negligence, or for fraud.
            </p>
            <p className="mt-4">Subject to that, and to the fullest extent Omani law permits:</p>
            <ul className="mt-3 list-disc space-y-1 ps-6">
              <li>the website is provided on an "as is" basis without warranties of any kind;</li>
              <li>we are not liable for loss of profit, business, revenue, anticipated savings or data, or any indirect or consequential loss arising from your use of the website;</li>
              <li>our total liability in connection with your use of the website is limited to OMR 100.</li>
            </ul>
            <p className="mt-4">Liability under client projects is governed separately by your signed agreement.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">11. Privacy</h2>
            <p className="mt-4">
              See our <a href="/privacy-policy" className="underline underline-offset-2">Privacy Policy</a> and <a href="/cookie-policy" className="underline underline-offset-2">Cookie Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">12. Governing law and jurisdiction</h2>
            <p className="mt-4">
              These terms, and any dispute arising from them, are governed by the laws of the Sultanate of Oman. The courts of the Sultanate of Oman have exclusive jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">13. General</h2>
            <p className="mt-4">
              If any provision is found unenforceable, the rest continues in force. Our failure to enforce a term is not a waiver of it. These terms are the entire agreement between us regarding use of the website.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-wafa-deep">14. Contact</h2>
            <p className="mt-4">
              Wafa Solutions LLC, Sultanate of Oman.<br />
              <a href="mailto:support@wafa.om" className="underline underline-offset-2">support@wafa.om</a>
            </p>
          </section>
        </article>
      </InnerBlock>
    </div>
  );
}
